import { IHackathonResponse } from "api/hackathon";
import { Dayjs } from "dayjs";
import { useMemo } from "react";

import { getTzFormat, getCurrentUTCTime, initDate } from "utils/dateUtils";

export const isBeforeToday = (
  time?: Dayjs,
  _tz: string = "America/Chicago"
): boolean => {
  if (!time) return false;
  return getCurrentUTCTime(_tz)?.valueOf() < time?.valueOf();
};
export const isAfterToday = (
  time?: Dayjs,
  _tz: string = "America/Chicago"
): boolean => {
  if (!time) return false;
  return getCurrentUTCTime(_tz)?.valueOf() > time?.valueOf();
};

export const useDeadline = (hackathon?: IHackathonResponse) => {
  return useMemo(() => {
    const timezoneFormat = getTzFormat(hackathon?.essentials?.timeZone);
    const timezone = timezoneFormat.value;
    const startDate = initDate(hackathon?.submission?.startDate, timezone);
    const deadline = initDate(hackathon?.submission?.deadline, timezone);
    const startVoteDate = initDate(hackathon?.vote?.startVote, timezone);
    const endVoteDate = initDate(hackathon?.vote?.endVote, timezone);
    const startJudgeDate = initDate(
      hackathon?.judging?.judgingDate?.startDate,
      timezone
    );
    const endJudgeDate = initDate(
      hackathon?.judging?.judgingDate?.endDate,
      timezone
    );
    const announceDate = initDate(hackathon?.prizes?.onDate, timezone);
    const isPublish = hackathon?.status === "publish";

    // ------------------------ Condition -------------------------------

    const isBeforeSubmission =
      !!startDate && isBeforeToday(startDate, timezone);
    const onSubmissionTime =
      !!deadline &&
      isAfterToday(startDate, timezone) &&
      isBeforeToday(deadline, timezone);

    const isBeforePublicVote =
      !!startVoteDate &&
      isAfterToday(startDate, timezone) &&
      isBeforeToday(startVoteDate, timezone);

    const onPublicVotingTime =
      !!startVoteDate &&
      isAfterToday(startVoteDate, timezone) &&
      isBeforeToday(endVoteDate, timezone);

    let isBeforeJudging = false;
    if (endVoteDate) {
      isBeforeJudging =
        isBeforeToday(startJudgeDate, timezone) &&
        isAfterToday(endVoteDate, timezone);
    } else {
      isBeforeJudging =
        !!deadline &&
        isBeforeToday(startJudgeDate, timezone) &&
        isAfterToday(deadline, timezone);
    }

    const onJudgingTime =
      !!startJudgeDate &&
      isAfterToday(startJudgeDate, timezone) &&
      isBeforeToday(endJudgeDate, timezone);

    const isBeforeWinnerAnnounce =
      !!announceDate && isBeforeToday(announceDate, timezone);

    const isEnded = isAfterToday(announceDate, timezone);
    const isEndSubmission = !!deadline && isAfterToday(deadline, timezone);

    return {
      timezoneFormat,
      isEndSubmission,
      isPublish,
      isEnded,
      isBeforeWinnerAnnounce,
      startDate,
      deadline,
      startVoteDate,
      endVoteDate,
      startJudgeDate,
      endJudgeDate,
      announceDate,
      isBeforeSubmission,
      isBeforePublicVote,
      isBeforeJudging,
      onSubmissionTime,
      onPublicVotingTime,
      onJudgingTime,
    };
  }, [hackathon]);
};
